import {
  MedCardType,
  MedScreenName,
  trackCareTreatmentCardDisplayed,
} from "../../nativeAnalytics/medAnalytics";
import { useTrackOnceWithRetry } from "../../nativeAnalytics/hooks/useTrackOnceWithRetry";

import type { TreatmentOverview } from "./useTreatmentOverviews";

const logPrefix = "[CareTabAnalytics]";
const STATUS_TYPENAME_PREFIX = "TreatmentOverviewStatus";

/**
 * Maps a GraphQL treatment status typename to the stable short name used
 * as `cardState` on `MedCardDisplayedProto`
 * (e.g. `TreatmentOverviewStatusShipped` → `"Shipped"`).
 */
export function getTreatmentCardState(
  status: TreatmentOverview["status"] | null | undefined
): string | undefined {
  const typename = status?.__typename;
  if (!typename?.startsWith(STATUS_TYPENAME_PREFIX)) {
    return undefined;
  }

  const cardState = typename.slice(STATUS_TYPENAME_PREFIX.length);
  return cardState || undefined;
}

/**
 * Fires `medCardDisplayed` once when the treatment card that calls this
 * hook mounts. Caller contract: call from a LEAF card component that only
 * mounts once the treatment is ready to render (same pattern as
 * `useTrackCareTabViewed`). Same-mount dedup / bridge retry is handled by
 * `useTrackOnceWithRetry`.
 */
export function useTrackMedCardDisplayed(treatment: TreatmentOverview) {
  const treatmentName = treatment.treatmentName ?? undefined;
  const cardState = getTreatmentCardState(treatment.status);

  useTrackOnceWithRetry({
    enabled: true,
    dispatch: () =>
      trackCareTreatmentCardDisplayed({ treatmentName, cardState }),
    logPrefix,
    logContext: {
      screenName: MedScreenName.CareTab,
      cardType: MedCardType.TreatmentCard,
      treatmentName,
      cardState,
    },
  });
}
